'use client';
import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import StatusBadge from '@/components/ui/StatusBadge';
import StarRating from '@/components/ui/StarRating';
import BookingModal from './BookingModal';

const services = [
  {
    id: 'svc-001',
    name: 'Premium Butler Service',
    icon: 'UserIcon',
    category: 'Personal',
    rating: 4.9,
    reviews: 312,
    status: 'available' as const,
    description: 'White-glove personal butler available for events, daily assistance, travel arrangements, and executive support.',
    features: ['Event Planning', 'Travel Coordination', 'Personal Shopping', '24/7 On-Call'],
    rate: 85,
    rateUnit: '/hour',
    minBooking: '4 hours',
    color: 'bg-indigo-50 border-indigo-100',
    iconColor: 'text-indigo-600',
    iconBg: 'bg-indigo-100',
  },
  {
    id: 'svc-002',
    name: 'Professional Chef',
    icon: 'HomeModernIcon',
    category: 'Culinary',
    rating: 4.8,
    reviews: 189,
    status: 'available' as const,
    description: 'Certified private chef for intimate dinners, meal prep services, cooking classes, and catering for events up to 50 guests.',
    features: ['Private Dining', 'Meal Prep', 'Cooking Classes', 'Event Catering'],
    rate: 120,
    rateUnit: '/session',
    minBooking: '3 hours',
    color: 'bg-amber-50 border-amber-100',
    iconColor: 'text-amber-600',
    iconBg: 'bg-amber-100',
  },
  {
    id: 'svc-003',
    name: 'Luxury Car Service',
    icon: 'TruckIcon',
    category: 'Transport',
    rating: 4.7,
    reviews: 445,
    status: 'available' as const,
    description: 'Chauffeured luxury vehicles including sedans, SUVs, and limousines. Airport transfers, corporate travel, and special occasions.',
    features: ['Airport Transfers', 'Corporate Travel', 'Event Transport', 'Hourly Charter'],
    rate: 95,
    rateUnit: '/hour',
    minBooking: '2 hours',
    color: 'bg-slate-50 border-slate-100',
    iconColor: 'text-slate-600',
    iconBg: 'bg-slate-100',
  },
  {
    id: 'svc-004',
    name: 'Spa & Wellness',
    icon: 'HeartIcon',
    category: 'Wellness',
    rating: 4.9,
    reviews: 267,
    status: 'available' as const,
    description: 'In-home spa treatments including Swedish massage, aromatherapy, facial treatments, and full wellness packages.',
    features: ['Massage Therapy', 'Facial Treatments', 'Aromatherapy', 'Wellness Packages'],
    rate: 110,
    rateUnit: '/session',
    minBooking: '60 min',
    color: 'bg-rose-50 border-rose-100',
    iconColor: 'text-rose-600',
    iconBg: 'bg-rose-100',
  },
  {
    id: 'svc-005',
    name: 'Event Coordination',
    icon: 'CalendarDaysIcon',
    category: 'Events',
    rating: 4.8,
    reviews: 134,
    status: 'booked' as const,
    description: 'Full-service event planning and day-of coordination for corporate events, weddings, private parties, and celebrations.',
    features: ['Venue Sourcing', 'Vendor Management', 'Day-of Coordination', 'Budget Planning'],
    rate: 150,
    rateUnit: '/day',
    minBooking: '1 day',
    color: 'bg-emerald-50 border-emerald-100',
    iconColor: 'text-emerald-600',
    iconBg: 'bg-emerald-100',
  },
  {
    id: 'svc-006',
    name: 'Security Personnel',
    icon: 'ShieldCheckIcon',
    category: 'Security',
    rating: 4.6,
    reviews: 78,
    status: 'available' as const,
    description: 'Licensed and trained security professionals for residential properties, events, VIP protection, and corporate security.',
    features: ['Event Security', 'VIP Protection', 'Property Guard', 'Corporate Security'],
    rate: 75,
    rateUnit: '/hour',
    minBooking: '4 hours',
    color: 'bg-blue-50 border-blue-100',
    iconColor: 'text-blue-600',
    iconBg: 'bg-blue-100',
  },
];

export default function HospitalityCards() {
  const [bookingTarget, setBookingTarget] = useState<(typeof services)[0] | null>(null);

  return (
    <div>
      <div className="mb-6">
        <p className="text-sm text-muted-foreground">
          <span className="font-semibold text-foreground">{services.length}</span> hospitality services available
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-3 gap-5">
        {services.map((svc) => (
          <div
            key={svc.id}
            className={`rounded-xl border ${svc.color} p-5 hover:shadow-card-hover transition-all duration-200 bg-white group`}
          >
            <div className="flex items-start justify-between mb-3">
              <div className={`p-2.5 rounded-xl ${svc.iconBg}`}>
                <Icon name={svc.icon as 'UserIcon'} size={22} className={svc.iconColor} />
              </div>
              <StatusBadge status={svc.status} size="sm" />
            </div>

            <div className="mb-1">
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{svc.category}</span>
              <h3 className="font-display font-semibold text-lg text-foreground">{svc.name}</h3>
            </div>

            <div className="flex items-center gap-2 mb-3">
              <StarRating value={svc.rating} size={13} />
              <span className="text-xs text-muted-foreground">{svc.rating} ({svc.reviews} reviews)</span>
            </div>

            <p className="text-sm text-muted-foreground mb-4 leading-relaxed line-clamp-3">{svc.description}</p>

            <div className="flex flex-wrap gap-1.5 mb-4">
              {svc.features.map((f) => (
                <span key={`${svc.id}-feat-${f}`} className="text-xs bg-white border border-border text-foreground px-2 py-0.5 rounded-full">
                  {f}
                </span>
              ))}
            </div>

            <div className="flex items-center justify-between pt-3 border-t border-border/50">
              <div>
                <span className="font-display font-bold text-xl text-primary tabular-nums">${svc.rate}</span>
                <span className="text-xs text-muted-foreground ml-1">{svc.rateUnit}</span>
                <p className="text-xs text-muted-foreground">Min. {svc.minBooking}</p>
              </div>
              <button
                onClick={() => setBookingTarget(svc)}
                disabled={svc.status === 'booked'}
                className={`px-4 py-2 text-sm font-semibold rounded-lg transition-all duration-150 active:scale-95 ${
                  svc.status === 'booked' ?'bg-muted text-muted-foreground cursor-not-allowed' :'bg-primary text-white hover:bg-primary-light'
                }`}
              >
                {svc.status === 'booked' ? 'Unavailable' : 'Book Now'}
              </button>
            </div>
          </div>
        ))}
      </div>

      {bookingTarget && (
        <BookingModal
          open={!!bookingTarget}
          onClose={() => setBookingTarget(null)}
          serviceType="hospitality"
          serviceName={bookingTarget.name}
          serviceRate={bookingTarget.rate}
          serviceRateUnit={bookingTarget.rateUnit}
          serviceImage=""
          serviceImageAlt=""
        />
      )}
    </div>
  );
}